import { Link } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   SeoLandingPage — Light Water (restructured)

   Page flow:
     1. Centered hero (light, gradient title, perks chip row)
     2. Live templates strip (4 thumbs, light card holders)
     3. How it works (3 step cards in light grid)
     4. Feature stream (alternating image | text rows on light bg)
     5. ONE brand-aqua CTA banner (white text only — no dark text on blue)
     6. FAQ section (light water glass)
     7. Explore more (light pills, dark text — never dark on aqua)

   Color contract:
     - White / frosted surfaces  →  brand-strong / text-body / text-muted
     - Brand-aqua filled bg (PromoSection only)  →  WHITE text only
     - Pills always live on light surfaces with brand-700 text
   ═══════════════════════════════════════════════════════════════════════ */

const placeholderShimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

/* ─── Shared mixins ─────────────────────────────────────────────────── */

const waterGlass = css`
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
`;

const frostedPill = css`
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
`;

const primaryPill = css`
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow: var(--shadow-cta);
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Page shell ────────────────────────────────────────────────────── */

export const Page = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 20px 80px;
  display: flex;
  flex-direction: column;
  padding-top: 48px;
  gap: 56px;

  @media (max-width: 768px) {
    padding: 20px 12px 56px;
    padding-top: 48px;
    gap: 40px;
  }
`;

/* ─── 1. Centered hero ─────────────────────────────────────────────── */

export const Hero = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
  padding: 56px 24px 40px;

  @media (max-width: 768px) {
    padding: 32px 16px 28px;
    gap: 14px;
  }
`;

/* Decorative water-line separator — drops below the hero before the
   templates strip, mirroring the HomePage rhythm. */
export const WaterSeparator = styled.img`
  position: relative;
  display: block;
  width: 100vw;
  max-width: 100vw;
  /* Break out of the constrained Page container so the image spans the
     full viewport width regardless of parent max-width + padding. */
  left: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-top: -12px;
  margin-bottom: -8px;
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
  filter: drop-shadow(0 -10px 18px rgba(var(--glass-white-rgb), 0.85))
    drop-shadow(0 6px 18px rgba(var(--color-brand-400-rgb), 0.18));
  z-index: 0;
`;

export const Eyebrow = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  box-shadow: var(--shadow-micro);

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-brand-500);
    box-shadow: 0 0 8px rgba(var(--color-brand-400-rgb), 0.6);
    margin-right: 8px;
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: clamp(34px, 5.4vw, 56px);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: normal;
  max-width: 22ch;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 2px 4px rgba(var(--color-brand-600-rgb), 0.14))
    drop-shadow(0 1px 2px rgba(var(--glass-white-rgb), 0.7));

  @media (max-width: 768px) {
    max-width: 18ch;
  }
`;

export const Intro = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 17px;
  font-weight: 500;
  line-height: normal;
  max-width: 64ch;
`;

export const SupportingCopy = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  max-width: 60ch;
`;

/* ─── Action buttons (used in hero + promo) ────────────────────────── */

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  margin-top: 8px;
`;

const actionPillBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 24px;
  border-radius: 999px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  position: relative;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);
`;

export const PrimaryAction = styled(Link)`
  ${actionPillBase};
  ${primaryPill};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4.5s infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

export const PrimaryActionButton = styled.button`
  ${actionPillBase};
  ${primaryPill};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4.5s infinite;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

const secondaryPill = css`
  ${actionPillBase};
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }
`;

export const SecondaryAction = styled(Link)`
  ${secondaryPill};
`;

export const SecondaryActionButton = styled.button`
  ${secondaryPill};
`;

/* ─── Hero perks (light pills, dark text) ──────────────────────────── */

export const PerkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
  margin-top: 4px;
`;

export const PerkPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  ${frostedPill};
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;

  &::before {
    content: "✓";
    margin-right: 6px;
    color: var(--color-brand-500);
    font-weight: 900;
  }
`;

/* ─── Section blocks (templates strip + how-it-works + related) ────── */

export const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  max-width: 720px;
  margin: 0 auto;
`;

export const SectionKicker = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: center;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: var(--shadow-micro);
`;

export const WorkflowTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;
`;

export const WorkflowText = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 500;
  line-height: normal;
  max-width: 56ch;
`;

/* ─── 2. Templates strip ───────────────────────────────────────────── */

export const TemplatesStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const TemplateFrame = styled.div`
  position: relative;

  border-radius: 24px;
  height: fit-content;
  overflow: hidden;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  isolation: isolate;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &:hover {
    transform: translateY(-3px);
    border-color: var(--color-line-strong);
    box-shadow:
      var(--shadow-panel),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
  }
`;

export const TemplatePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(var(--color-brand-200-rgb), 0.18) 25%,
    rgba(var(--color-brand-200-rgb), 0.36) 50%,
    rgba(var(--color-brand-200-rgb), 0.18) 75%
  );
  background-size: 200% 100%;
  animation: ${placeholderShimmer} 1.6s linear infinite;
`;

/* ─── 3. How it works grid ─────────────────────────────────────────── */

export const QuickStepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const QuickStepCard = styled.section`
  ${waterGlass};
  border-radius: 22px;
  padding: 24px 24px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy);

  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-line-strong);
  }
`;

export const QuickStepTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const QuickStepNumber = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const QuickStepTitle = styled.h3`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.015em;
  line-height: normal;
`;

export const QuickStepText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13.5px;
  font-weight: 500;
  line-height: normal;
`;

/* ─── 4. Feature stream — alternating media | content (no card wrap) ─ */

export const FeatureStream = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;

  @media (max-width: 768px) {
    gap: 36px;
  }
`;

export const FeatureRow = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 36px;
  align-items: center;
  direction: ${({ $reverse }) => ($reverse ? "rtl" : "ltr")};

  > * {
    direction: ltr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    direction: ltr;
    gap: 22px;
  }
`;

export const FeatureMedia = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${TemplateFrame} {
    width: 100%;
    max-width: 320px;
    aspect-ratio: 9 / 14;
  }
`;

export const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const CardLabel = styled.p`
  margin: 0;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

export const FeatureTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(22px, 2.6vw, 30px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: normal;
`;

export const FeatureText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
`;

export const BulletList = styled.ul`
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    position: relative;
    padding-left: 24px;
    color: var(--color-text-body);
    font-family: var(--font-family-primary);
    font-size: 14.5px;
    font-weight: 500;
    line-height: normal;

    &::before {
      content: "";
      position: absolute;
      left: 4px;
      top: 9px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: linear-gradient(
        180deg,
        var(--color-brand-300) 0%,
        var(--color-brand-500) 100%
      );
      box-shadow:
        0 2px 6px rgba(var(--color-brand-500-rgb), 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.55);
    }

    strong {
      color: var(--color-text-strong);
      font-weight: 700;
    }
  }
`;

/* ─── 5. Single brand-aqua CTA banner ──────────────────────────────── */

export const PromoSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 36px 36px 32px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 60%,
    var(--color-brand-700) 100%
  );
  color: var(--glass-text-on-pill);
  box-shadow:
    0 32px 60px -22px rgba(var(--color-brand-800-rgb), 0.5),
    0 12px 26px rgba(var(--color-brand-800-rgb), 0.26),
    inset 0 2px 0 rgba(255, 255, 255, 0.5),
    inset 0 -8px 18px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  text-align: center;
  align-items: center;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 36px;
    right: 36px;
    height: 16px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  /* Inside the colored callout, secondary button becomes white frost so it
     stays readable. NEVER dark text on aqua. */
  ${ActionRow} ${SecondaryAction} {
    background: var(--glass-base-1);
    border-color: var(--glass-edge);
    color: var(--color-brand-700);

    &:hover {
      background: var(--glass-base-1);
      border-color: var(--glass-edge-strong);
    }
  }

  @media (max-width: 600px) {
    padding: 28px 22px 26px;
    border-radius: 22px;
  }
`;

export const PromoCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 640px;
  align-items: center;
`;

export const PromoTitle = styled.h2`
  margin: 0;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
`;

export const PromoText = styled.p`
  margin: 0;
  color: rgba(var(--glass-white-rgb), 0.92);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
`;

/* ─── 6. FAQ ──────────────────────────────────────────────────────── */

export const FaqSection = styled.section`
  ${waterGlass};
  border-radius: 24px;
  padding: 32px 36px 26px;

  @media (max-width: 600px) {
    padding: 26px 22px 22px;
    border-radius: 20px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 18px;
  text-align: center;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.025em;
`;

export const FaqItem = styled.article`
  padding: 16px 0;
  border-bottom: 1px solid var(--color-line);

  &:last-child {
    border-bottom: none;
  }
`;

export const Question = styled.h3`
  margin: 0 0 6px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: normal;
`;

export const Answer = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 400;
  line-height: normal;
`;

/* ─── 7. Explore more — light surface, dark text ──────────────────── */

export const RelatedSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

export const TopicCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 880px;
`;

export const TopicPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;
`;

export const LinkCloud = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 880px;
  margin-top: 4px;
`;

export const LinkPill = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 9px 16px;
  border-radius: 999px;
  ${frostedPill};
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-decoration: none;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }
`;

/* ─── Legacy exports (kept stable to avoid downstream breakage) ───── */

export const HeroVisualColumn = styled.div``;
export const HeroTemplateGrid = styled.div``;
export const HeroCallout = styled.div``;
export const HeroBulletList = styled.ul``;
export const HeroCard = styled.div``;
export const ConversionTitle = styled.h2``;
export const ConversionText = styled.p``;
export const CardList = styled.ul``;
export const StepList = styled.div``;
export const StepItem = styled.div``;
export const StepNumber = styled.div``;
export const StepContent = styled.div``;
export const StepTitle = styled.h3``;
export const StepText = styled.p``;
export const SectionGrid = styled.div``;
export const SectionCard = styled.section``;
export const WorkflowSection = styled.section``;
export const WorkflowHeader = styled.div``;
